// Takeoff grid column model.
//   type: "index" | "text" | "number" | "select" | "bool" | "calc"
//   group: "frozen" (always shown) | "inputs" | "quantities" | "hours" | "costs"
//   field: EstimateLine key (editable) · calcKey: calcLine() result key (read-only)
//   defaultable: compared with getLineDefaults() → blue text + reset when overridden
import { CONDITIONS, TOOLS } from "../../../../mock/config.mock";
import { fmtCurrency, fmtHrs, fmtQty } from "../../../../utils/format";

export const COLUMN_GROUPS = [
  { key: "inputs", label: "Inputs" },
  { key: "quantities", label: "Quantities" },
  { key: "hours", label: "Hours" },
  { key: "costs", label: "Costs" },
];

const UNIT_OPTIONS = ["SF", "LF", "EA"].map((u) => ({ value: u, label: u }));

/** Builds the dropdown option lists once per config. */
export const buildOptions = (config) => ({
  surfaceId: config.surfaces.map((s) => ({ value: s.id, label: s.name })),
  unit: UNIT_OPTIONS,
  sspcPrep: config.finishingMasking.sspc.map((s) => ({ value: s.id, label: s.label })),
  access: config.accessBands.map((b) => ({ value: b.id, label: b.label })),
  finishLevel: config.finishingMasking.finishLevels.map((f) => ({ value: f.id, label: f.id === "—" ? "—" : f.label })),
  condition: CONDITIONS.map((c) => ({ value: c, label: c })),
  tool: TOOLS.map((t) => ({ value: t, label: t })),
  finishProductId: config.products.filter((p) => p.type === "FINISH" && p.active).map((p) => ({ value: p.id, label: p.id })),
  primerProductId: [{ value: "", label: "— None —" }, ...config.products.filter((p) => p.type === "PRIMER" && p.active).map((p) => ({ value: p.id, label: p.id }))],
});

/** Number-field validation used while editing (before the value is committed). */
export const validateCellValue = (field, value) => {
  if (value === "" || value == null || Number.isNaN(Number(value))) return "Enter a number";
  const n = Number(value);
  if (field === "nativeQty" && !(n > 0)) return "Qty must be greater than 0";
  if (field === "coats" && (!Number.isInteger(n) || n < 0 || n > 5)) return "Coats must be a whole number 0–5";
  if (n < 0) return "Must be 0 or more";
  return null;
};

const calc = (key, label, group, format, width = 92) => ({ key, label, group, type: "calc", calcKey: key, format, width, align: "right" });

export const COLUMNS = [
  { key: "index", label: "#", group: "frozen", type: "index", width: 46 },
  { key: "area", label: "Area / Room", group: "frozen", type: "text", field: "area", width: 190 },
  { key: "surfaceId", label: "Surface", group: "frozen", type: "select", field: "surfaceId", width: 232 },
  { key: "unit", label: "Unit", group: "inputs", type: "select", field: "unit", width: 70 },
  { key: "nativeQty", label: "Native Qty", group: "inputs", type: "number", field: "nativeQty", width: 96, align: "right", format: fmtQty },
  calc("sfQty", "SF Qty", "quantities", fmtQty, 96),
  { key: "coats", label: "Coats", group: "inputs", type: "number", field: "coats", width: 64, align: "right", defaultable: true },
  { key: "primer", label: "Primer?", group: "inputs", type: "bool", field: "primer", width: 70, defaultable: true },
  { key: "topcoat", label: "Topcoat?", group: "inputs", type: "bool", field: "topcoat", width: 76, defaultable: true },
  { key: "sspcPrep", label: "SSPC Prep", group: "inputs", type: "select", field: "sspcPrep", width: 104, defaultable: true },
  { key: "subbed", label: "Subbed?", group: "inputs", type: "bool", field: "subbed", width: 74, defaultable: true },
  { key: "access", label: "Access", group: "inputs", type: "select", field: "access", width: 140, defaultable: true },
  { key: "finishLevel", label: "Finish Lvl", group: "inputs", type: "select", field: "finishLevel", width: 92, defaultable: true },
  { key: "condition", label: "Condition", group: "inputs", type: "select", field: "condition", width: 140, defaultable: true },
  { key: "tool", label: "Field Tool", group: "inputs", type: "select", field: "tool", width: 118, defaultable: true },
  { key: "finishProductId", label: "Finish Product", group: "inputs", type: "select", field: "finishProductId", width: 188, defaultable: true },
  { key: "primerProductId", label: "Primer Product", group: "inputs", type: "select", field: "primerProductId", width: 164, defaultable: true },
  calc("finishGal", "Finish Gal", "quantities", fmtQty),
  calc("primerGal", "Primer Gal", "quantities", fmtQty),
  calc("fieldHrs", "Field Hrs", "hours", fmtHrs, 84),
  calc("cutHrs", "Cut Hrs", "hours", fmtHrs, 80),
  { ...calc("prepHrs", "Prep Hrs", "hours", fmtHrs, 84), calcKey: "prepAll" },
  calc("maskHrs", "Mask Hrs", "hours", fmtHrs, 84),
  calc("totalHrs", "Total Hrs", "hours", fmtHrs, 88),
  calc("laborCost", "Labor $", "costs", fmtCurrency, 112),
  calc("subCost", "Sub $", "costs", fmtCurrency, 104),
  calc("materialCost", "Material $", "costs", fmtCurrency, 112),
  calc("lineTotal", "Line Total $", "costs", fmtCurrency, 124),
];

/** Value of a calc column for a result (prep column = condition/SSPC prep + finish-level hours). */
export const calcValue = (col, result) => {
  if (!result) return null;
  if (col.calcKey === "prepAll") return result.prepHrs + result.finishHrs;
  return result[col.calcKey];
};

/** Footer totals keyed like calcValue. */
export const totalValue = (col, totals) => {
  const map = {
    sfQty: totals.sfQty, finishGal: totals.finishGal, primerGal: totals.primerGal, fieldHrs: totals.fieldHrs,
    cutHrs: totals.cutHrs, prepAll: totals.prepHrs, maskHrs: totals.maskHrs, totalHrs: totals.totalHrs,
    laborCost: totals.laborCost, subCost: totals.subCost, materialCost: totals.materialCost, lineTotal: totals.lineTotal,
  };
  return map[col.calcKey];
};
